// src/lib/image-utils.ts

import { Track } from "@/types/track";
import { getLabelPlaceholderImageUrl } from "./label-placeholders";

/**
 * Get the best available track image URL from various sources
 * @param track - The track object
 * @param size - Optional size parameter for the image (default: 40)
 * @param label - Optional label object for fallback placeholder
 * @returns The best available image URL or a fallback
 */
export function getTrackImageUrl(
  track: Track,
  size: number = 40,
  label?: any
): string {
  // Priority order for image sources
  const imageSources = [
    track.cover_art_url,
    track.artwork_url,
    (track.metadata as any)?.cover_art_url,
    (track.metadata as any)?.artwork_url,
  ];

  // Find the first valid image URL
  for (const source of imageSources) {
    if (source && typeof source === "string" && source.trim().length > 0) {
      // Check if it's a valid URL or data URI
      if (source.startsWith("http") || source.startsWith("data:")) {
        return source;
      }
    }
  }

  // Try to generate a better fallback using external services
  const externalImageUrl = getExternalTrackImageUrl(track, size);
  if (externalImageUrl) {
    return externalImageUrl;
  }

  // Try label placeholder image as fallback
  const labelImageUrl = getLabelPlaceholderImageUrl(label);
  if (labelImageUrl) {
    return labelImageUrl;
  }

  // If no valid image found, return a fallback
  return getFallbackImageUrl(track, size);
}

/**
 * Try to get track image from external services
 * @param track - The track object
 * @param size - Size parameter for the image
 * @returns External image URL or null
 */
function getExternalTrackImageUrl(track: Track, size: number): string | null {
  // For now, we'll use a music-themed placeholder service
  // In the future, this could integrate with Spotify, Last.fm, or other APIs

  const title = track.title || "";
  const artist = track.artist || "";

  // Only try external services if we have both title and artist
  if (!title || !artist) {
    return null;
  }

  // Use a music-themed placeholder that looks more like album art
  const genre = track.genre || "music";
  const genreColors = {
    rock: "ef4444",
    pop: "3b82f6",
    electronic: "8b5cf6",
    "hip-hop": "f59e0b",
    jazz: "10b981",
    classical: "6b7280",
    country: "84cc16",
    "r&b": "ec4899",
    reggae: "f97316",
    blues: "6366f1",
  };

  // Handle both string and array genre formats
  const genreString = Array.isArray(genre)
    ? genre.join(" ")
    : String(genre || "");
  const genreKey = Object.keys(genreColors).find((key) =>
    genreString.toLowerCase().includes(key)
  );
  const color = genreKey
    ? genreColors[genreKey as keyof typeof genreColors]
    : "6366f1";

  // Create a more sophisticated placeholder with gradient
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    title.charAt(0).toUpperCase()
  )}&size=${size}&background=${color}&color=ffffff&bold=true`;
}

/**
 * Get a fallback image URL when no track image is available
 * @param track - The track object
 * @param size - Size parameter for the image
 * @returns A fallback image URL
 */
export function getFallbackImageUrl(track: Track, size: number = 40): string {
  // Create a more meaningful fallback based on track metadata
  const trackId = String(track.id);
  const title = track.title || "Unknown Track";
  const artist = track.artist || "Unknown Artist";
  const genre = track.genre || "music";

  // Create initials from title (first letter of each word, max 2)
  const titleInitials = title
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // If title initials are too short, use artist initials
  const displayText =
    titleInitials.length >= 2
      ? titleInitials
      : artist
          .split(" ")
          .map((word) => word.charAt(0))
          .join("")
          .toUpperCase()
          .slice(0, 2);

  // Create a deterministic but varied fallback based on track properties
  const seed = `${trackId}-${title}-${artist}`.replace(/[^a-zA-Z0-9]/g, "");

  // Use different colors based on genre for visual variety
  const genreColors = {
    rock: "ef4444", // red
    pop: "3b82f6", // blue
    electronic: "8b5cf6", // purple
    "hip-hop": "f59e0b", // amber
    jazz: "10b981", // emerald
    classical: "6b7280", // gray
    country: "84cc16", // lime
    "r&b": "ec4899", // pink
    reggae: "f97316", // orange
    blues: "6366f1", // indigo
  };

  // Find matching color for genre (handle both string and array)
  const genreString = Array.isArray(genre)
    ? genre.join(" ")
    : String(genre || "");
  const genreKey = Object.keys(genreColors).find((key) =>
    genreString.toLowerCase().includes(key)
  );
  const color = genreKey
    ? genreColors[genreKey as keyof typeof genreColors]
    : "6366f1";

  // Use a more reliable placeholder service
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    displayText
  )}&size=${size}&background=${color}&color=ffffff&bold=true`;
}

/**
 * Get track image with error handling
 * @param track - The track object
 * @param size - Size parameter for the image
 * @param label - Optional label object for fallback placeholder
 * @returns Object with image URL and error handling
 */
export function getTrackImageWithFallback(
  track: Track,
  size: number = 40,
  label?: any
) {
  const imageUrl = getTrackImageUrl(track, size, label);

  return {
    src: imageUrl,
    alt: `${track.title} by ${track.artist}`,
    onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
      const target = e.target as HTMLImageElement;
      // Try label placeholder first, then fallback
      const labelImageUrl = getLabelPlaceholderImageUrl(label);
      if (labelImageUrl && target.src !== labelImageUrl) {
        target.src = labelImageUrl;
      } else if (target.src !== getFallbackImageUrl(track, size)) {
        target.src = getFallbackImageUrl(track, size);
      }
    },
  };
}

/**
 * Check if a track has a real image (not placeholder)
 * @param track - The track object
 * @returns Boolean indicating if track has a real image
 */
export function hasRealTrackImage(track: Track): boolean {
  const imageSources = [
    track.cover_art_url,
    track.artwork_url,
    (track.metadata as any)?.cover_art_url,
    (track.metadata as any)?.artwork_url,
  ];

  return imageSources.some(
    (source) =>
      source &&
      typeof source === "string" &&
      source.trim().length > 0 &&
      (source.startsWith("http") || source.startsWith("data:"))
  );
}

/**
 * Get track image component props with proper error handling
 * @param track - The track object
 * @param size - Size parameter for the image
 * @param className - Additional CSS classes
 * @param label - Optional label object for fallback placeholder
 * @returns Props object for img element
 */
export function getTrackImageProps(
  track: Track,
  size: number = 40,
  className: string = "",
  label?: any
) {
  const imageUrl = getTrackImageUrl(track, size, label);
  const fallbackUrl = getFallbackImageUrl(track, size);
  const labelImageUrl = getLabelPlaceholderImageUrl(label);

  return {
    src: imageUrl,
    alt: `${track.title} by ${track.artist}`,
    className: `object-cover ${className}`,
    onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
      const target = e.target as HTMLImageElement;
      // Try label placeholder first, then fallback
      if (labelImageUrl && target.src !== labelImageUrl) {
        target.src = labelImageUrl;
      } else if (target.src !== fallbackUrl) {
        target.src = fallbackUrl;
      }
    },
    loading: "lazy" as const,
  };
}

/**
 * Get fallback avatar URL for user profiles
 * @param name - User name or initials
 * @param size - Size parameter for the avatar
 * @returns Fallback avatar URL
 */
export function getFallbackAvatarUrl(name: string, size: number = 128): string {
  const initials = name
    .split(" ")
    .map((n) => n.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    initials
  )}&size=${size}&background=6366f1&color=ffffff&bold=true`;
}

/**
 * Handle image error by setting a fallback image
 * @param e - Image error event
 * @param fallbackUrl - URL to use as fallback
 */
export function handleImageError(
  e: React.SyntheticEvent<HTMLImageElement>,
  fallbackUrl: string
) {
  const target = e.target as HTMLImageElement;
  if (target.src !== fallbackUrl) {
    target.src = fallbackUrl;
  }
}
